import { useState, useEffect, FormEvent } from "react";

import {
  Container,
  Row,
  Col,
  Tooltip,
  Button,
  Form,
  Modal,
  OverlayTrigger,
  InputGroup,
} from "react-bootstrap";

import api from "../../services/api";
import Template from "../../template";
import { toast } from "react-toastify";

import * as S from "./styled";

interface Competencia {
  competencia: string | number;
  descricao: string;
  id: number;
}

function Competencias() {
  const [competencias, setCompetencias] = useState<Competencia[]>([]);
  const [todasCompetencias, setTodasCompetencias] = useState<Competencia[]>([]);

  const [competencia, setCompetencia] = useState("");
  const [descricao, setDescricao] = useState("");
  const [id, setId] = useState(0);

  // Estados para filtros
  const [filtroCompetencia, setFiltroCompetencia] = useState("");
  const [buscaDescricao, setBuscaDescricao] = useState("");

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Estados para modais
  const [show, setShow] = useState(false);
  const handleClose = () => {
    setShow(false);
    // Limpar campos
    setCompetencia("");
    setDescricao("");
    setId(0);
  };
  const handleShow = () => setShow(true);

  const [showEdit, setShowEdit] = useState(false);
  const handleCloseEdit = () => {
    setShowEdit(false);
    // Limpar campos
    setCompetencia("");
    setDescricao("");
    setId(0);
  };

  const [showDelete, setShowDelete] = useState(false);
  const handleCloseDelete = () => {
    setShowDelete(false);
    setId(0);
  };

  async function handleAddCompetencia(e: FormEvent) {
    e.preventDefault();

    if (!competencia || !descricao) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    try {
      const dataCompetencia = {
        competencia,
        descricao,
      };

      await api.post("/addCompetencias", dataCompetencia);

      handleClose();
      toast.success("Competência cadastrada com sucesso!");
      getCompetencias();
    } catch (error) {
      toast.error("Não foi possível cadastrar competência");
    }
  }

  async function handleGetCompetencia(e) {
    const competenciaId = e.currentTarget.dataset.id;
    
    try {
      const dataCompetenciaId = {
        id: competenciaId,
      };

      let response = await api.post("/pegarCompetencia", dataCompetenciaId);

      if (response.data && response.data[0]) {
        setCompetencia(response.data[0].competencia);
        setDescricao(response.data[0].descricao);
        setId(competenciaId);
        setShowEdit(true);
      }
    } catch (error) {
      toast.error("Erro ao carregar dados da competência");
    }
  }

  async function handleEditCompetencia(e: FormEvent) {
    e.preventDefault();

    if (!competencia || !descricao) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    try {
      const dataCompetencia = {
        competencia,
        descricao,
        id,
      };

      await api.put("/editCompetencia", dataCompetencia);

      handleCloseEdit();
      toast.success("Competência editada com sucesso!");
      getCompetencias();
    } catch (error) {
      toast.error("Não foi possível editar competência");
    }
  }

  function handleDeleteClick(e) {
    const competenciaId = e.currentTarget.dataset.id;
    setId(competenciaId);
    setShowDelete(true);
  }

  async function handleDeleteCompetencia(e: FormEvent) {
    e.preventDefault();

    try {
      await api.delete(`/deleteCompetencia/${id}`);
      handleCloseDelete();
      toast.success("Competência deletada com sucesso!");
      getCompetencias();
    } catch (error) {
      toast.error("Erro ao deletar competência");
    }
  }

  async function getCompetencias() {
    try {
      setLoading(true);
      let response = await api.get("/getCompetencias");
      setTodasCompetencias(response.data);
      aplicarFiltros(response.data);
    } catch (err) {
      toast.error("Erro ao carregar competências");
    } finally {
      setLoading(false);
    }
  }

  // Função para aplicar filtros e paginação
  const aplicarFiltros = (dados = todasCompetencias) => {
    let competenciasFiltradas = dados;

    // Filtro por número da competência
    if (filtroCompetencia) {
      competenciasFiltradas = competenciasFiltradas.filter(
        (comp) => comp.competencia.toString() === filtroCompetencia
      );
    }

    // Filtro por descrição
    if (buscaDescricao) {
      competenciasFiltradas = competenciasFiltradas.filter((comp) =>
        comp.descricao.toLowerCase().includes(buscaDescricao.toLowerCase())
      );
    }

    // Aplicar paginação
    const totalItens = competenciasFiltradas.length;
    const totalPaginas = Math.ceil(totalItens / itemsPerPage);
    const indiceInicial = (currentPage - 1) * itemsPerPage;
    const indiceFinal = indiceInicial + itemsPerPage;
    const competenciasPaginadas = competenciasFiltradas.slice(indiceInicial, indiceFinal);

    setCompetencias(competenciasPaginadas);
    setTotalItems(totalItens);
    setTotalPages(totalPaginas);
  };

  // Função para limpar filtros
  const limparFiltros = () => {
    setFiltroCompetencia("");
    setBuscaDescricao("");
    setCurrentPage(1);
  };

  // Funções de paginação
  const irParaPagina = (pagina) => {
    setCurrentPage(pagina);
  };

  const paginaAnterior = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const proximaPagina = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Aplicar filtros quando mudarem
  useEffect(() => {
    aplicarFiltros();
  }, [filtroCompetencia, buscaDescricao, currentPage, itemsPerPage]);

  useEffect(() => {
    getCompetencias();
  }, []);

  return (
    <Template>
      <S.MainWrapper>
        <Container fluid className="pt-4 pb-4" style={{ maxWidth: '95%' }}>
          <Row>
            <Col sm={12}>
              {/* Header com estatísticas */}
              <div className="shadow-sm p-4 mb-4 bg-white rounded">
                <Row>
                  <Col md={8}>
                    <div className="d-flex align-items-center">
                      <div className="bg-primary text-white rounded-circle p-3 mr-3">
                        <i className="fas fa-clipboard-list fa-2x"></i>
                      </div>
                      <div>
                        <h4 className="mb-1 font-weight-bold text-dark">Competências</h4>
                        <p className="mb-0 text-muted">Gerencie as competências do sistema</p>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center">
                      <div className="display-4 text-primary font-weight-bold">
                        {totalItems}
                      </div>
                      <small className="text-muted font-weight-bold">
                        {totalItems === 1 ? 'competência encontrada' : 'competências encontradas'}
                      </small>
                      {totalItems > 0 && (
                        <div className="mt-2">
                          <small className="text-info font-weight-bold">
                            Página {currentPage} de {totalPages} | 
                            Mostrando {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems}
                          </small>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Botão de adicionar competência */}
              <div className="shadow-sm p-3 mb-4 bg-white rounded">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-dark font-weight-bold">Ações Rápidas</h5>
                  <Button
                    variant="success"
                    size="lg"
                    onClick={handleShow}
                    className="shadow-sm"
                  >
                    <i className="fas fa-plus mr-2"></i>
                    Adicionar Competência
                  </Button>
                </div>
              </div>

              {/* Filtros */}
              <div className="shadow-sm p-4 mb-4 bg-white rounded">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 text-dark font-weight-bold">
                    <i className="fas fa-filter text-primary mr-2"></i>
                    Filtros e Busca
                  </h5>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={limparFiltros}
                    className="shadow-sm"
                  >
                    <i className="fas fa-times mr-1"></i>
                    Limpar Filtros
                  </Button>
                </div>

                <Row>
                  {/* Itens por página */}
                  <Col xs={12} md={3} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold text-dark">
                        <i className="fas fa-list text-primary mr-1"></i>
                        Itens por Página
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          as="select"
                          value={itemsPerPage}
                          onChange={(e) => {
                            setItemsPerPage(parseInt(e.target.value));
                            setCurrentPage(1);
                          }}
                          className="form-control-lg"
                        >
                          <option value={5}>5 por página</option>
                          <option value={10}>10 por página</option>
                          <option value={15}>15 por página</option>
                          <option value={20}>20 por página</option>
                          <option value={25}>25 por página</option>
                          <option value={50}>50 por página</option>
                        </Form.Control>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  {/* Filtro por competência */}
                  <Col xs={12} md={4} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold text-dark">
                        <i className="fas fa-hashtag text-primary mr-1"></i>
                        Filtrar por Competência
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          as="select"
                          value={filtroCompetencia}
                          onChange={(e) => {
                            setFiltroCompetencia(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="form-control-lg"
                        >
                          <option value="">Todas as Competências</option>
                          <option value="1">Competência 1</option>
                          <option value="2">Competência 2</option>
                          <option value="3">Competência 3</option>
                          <option value="4">Competência 4</option>
                          <option value="5">Competência 5</option>
                        </Form.Control>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  {/* Busca por descrição */}
                  <Col xs={12} md={5} className="mb-3">
                    <Form.Group>
                      <Form.Label className="font-weight-bold text-dark">
                        <i className="fas fa-search text-primary mr-1"></i>
                        Buscar por Descrição
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Prepend>
                          <InputGroup.Text>
                            <i className="fas fa-search"></i>
                          </InputGroup.Text>
                        </InputGroup.Prepend>
                        <Form.Control
                          type="text"
                          placeholder="Digite a descrição..."
                          value={buscaDescricao}
                          onChange={(e) => {
                            setBuscaDescricao(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="form-control-lg"
                        />
                        {buscaDescricao && (
                          <InputGroup.Append>
                            <Button
                              variant="outline-secondary"
                              onClick={() => {
                                setBuscaDescricao("");
                                setCurrentPage(1);
                              }}
                            >
                              <i className="fas fa-times"></i>
                            </Button>
                          </InputGroup.Append>
                        )}
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Tabela de competências */}
              <div className="shadow-sm bg-white rounded">
                <div className="p-4 border-bottom">
                  <h5 className="mb-0 text-dark font-weight-bold">
                    <i className="fas fa-table text-primary mr-2"></i>
                    Lista de Competências
                  </h5>
                </div>

                {loading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Carregando...</span>
                    </div>
                    <p className="mt-3 text-muted">Carregando competências...</p>
                  </div>
                ) : competencias.length === 0 ? (
                  <div className="text-center p-5">
                    <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">Nenhuma competência encontrada</h5>
                    <p className="text-muted">Tente ajustar os filtros ou adicione uma nova competência.</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th className="border-0 font-weight-bold text-dark">
                              <i className="fas fa-hashtag text-primary mr-1"></i>
                              Competência
                            </th>
                            <th className="border-0 font-weight-bold text-dark">
                              <i className="fas fa-align-left text-primary mr-1"></i>
                              Descrição
                            </th>
                            <th className="border-0 font-weight-bold text-dark text-center">
                              <i className="fas fa-cogs text-primary mr-1"></i>
                              Ações
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {competencias.map((c, idx) => (
                            <tr key={c.id}>
                              <td className="align-middle">
                                <div className="d-flex align-items-center">
                                  <div className="bg-primary text-white rounded-circle p-2 mr-3" style={{width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                    <strong>{c.competencia}</strong>
                                  </div>
                                  <span className="font-weight-bold text-dark">Competência {c.competencia}</span>
                                </div>
                              </td>
                              <td className="align-middle">
                                <span className="text-dark">{c.descricao}</span>
                              </td>
                              <td className="align-middle text-center">
                                <div className="btn-group" role="group">
                                  <OverlayTrigger
                                    overlay={<Tooltip id="tooltip-edit">Editar Competência</Tooltip>}
                                  >
                                    <Button
                                      variant="warning"
                                      size="sm"
                                      data-id={c.id}
                                      onClick={handleGetCompetencia}
                                      className="mr-1"
                                    >
                                      <i className="fas fa-edit"></i>
                                    </Button>
                                  </OverlayTrigger>
                                  
                                  <OverlayTrigger
                                    overlay={<Tooltip id="tooltip-delete">Deletar Competência</Tooltip>}
                                  >
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      data-id={c.id}
                                      onClick={handleDeleteClick}
                                    >
                                      <i className="fas fa-trash"></i>
                                    </Button>
                                  </OverlayTrigger>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Paginação */}
                    {totalPages > 1 && (
                      <div className="border-top p-4">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="text-muted">
                            <i className="fas fa-info-circle mr-2"></i>
                            Mostrando {competencias.length} de {totalItems} competências
                          </div>
                          <nav aria-label="Navegação de páginas">
                            <ul className="pagination pagination-sm mb-0">
                              {/* Botão Anterior */}
                              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={paginaAnterior}
                                  disabled={currentPage === 1}
                                >
                                  <i className="fas fa-chevron-left"></i>
                                </button>
                              </li>

                              {/* Números das páginas */}
                              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pagina) => {
                                // Mostrar apenas algumas páginas ao redor da atual
                                if (
                                  pagina === 1 ||
                                  pagina === totalPages ||
                                  (pagina >= currentPage - 2 && pagina <= currentPage + 2)
                                ) {
                                  return (
                                    <li
                                      key={pagina}
                                      className={`page-item ${currentPage === pagina ? 'active' : ''}`}
                                    >
                                      <button
                                        className="page-link"
                                        onClick={() => irParaPagina(pagina)}
                                      >
                                        {pagina}
                                      </button>
                                    </li>
                                  );
                                } else if (
                                  pagina === currentPage - 3 ||
                                  pagina === currentPage + 3
                                ) {
                                  return (
                                    <li key={pagina} className="page-item disabled">
                                      <span className="page-link">...</span>
                                    </li>
                                  );
                                }
                                return null;
                              })}

                              {/* Botão Próximo */}
                              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={proximaPagina}
                                  disabled={currentPage === totalPages}
                                >
                                  <i className="fas fa-chevron-right"></i>
                                </button>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Col>
          </Row>
        </Container>

        {/* Modal Adicionar Competência */}
        <Modal show={show} onHide={handleClose} size="lg">
          <Modal.Header closeButton className="bg-success text-white">
            <Modal.Title>
              <i className="fas fa-plus mr-2"></i>
              Adicionar Competência
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="font-weight-bold">
                      <i className="fas fa-hashtag text-primary mr-1"></i>
                      Número da Competência *
                    </Form.Label>
                    <Form.Control
                      as="select"
                      value={competencia}
                      onChange={(e) => setCompetencia(e.target.value)}
                      className="form-control-lg"
                    >
                      <option value="">Selecione a competência</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="font-weight-bold">
                      <i className="fas fa-align-left text-primary mr-1"></i>
                      Descrição da Competência *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Digite a descrição da competência"
                      value={descricao}
                      onChange={(e) => setDescricao(e.target.value)}
                      className="form-control-lg"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer className="bg-light">
            <Button variant="secondary" onClick={handleClose} size="lg">
              <i className="fas fa-times mr-2"></i>
              Cancelar
            </Button>
            <Button variant="success" onClick={handleAddCompetencia} size="lg">
              <i className="fas fa-save mr-2"></i>
              Adicionar
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal Editar Competência */}
        <Modal show={showEdit} onHide={handleCloseEdit} size="lg">
          <Modal.Header closeButton className="bg-warning text-white">
            <Modal.Title>
              <i className="fas fa-edit mr-2"></i>
              Editar Competência
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="font-weight-bold">
                      <i className="fas fa-hashtag text-primary mr-1"></i>
                      Número da Competência *
                    </Form.Label>
                    <Form.Control
                      as="select"
                      value={competencia}
                      onChange={(e) => setCompetencia(e.target.value)}
                      className="form-control-lg"
                    >
                      <option value="">Selecione a competência</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="font-weight-bold">
                      <i className="fas fa-align-left text-primary mr-1"></i>
                      Descrição da Competência *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Digite a descrição da competência"
                      value={descricao}
                      onChange={(e) => setDescricao(e.target.value)}
                      className="form-control-lg"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer className="bg-light">
            <Button variant="secondary" onClick={handleCloseEdit} size="lg">
              <i className="fas fa-times mr-2"></i>
              Cancelar
            </Button>
            <Button variant="warning" onClick={handleEditCompetencia} size="lg">
              <i className="fas fa-save mr-2"></i>
              Salvar Alterações
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal Confirmar Exclusão */}
        <Modal show={showDelete} onHide={handleCloseDelete}>
          <Modal.Header closeButton className="bg-danger text-white">
            <Modal.Title>
              <i className="fas fa-exclamation-triangle mr-2"></i>
              Confirmar Exclusão
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center p-4">
            <i className="fas fa-trash fa-3x text-danger mb-3"></i>
            <h5>Tem certeza que deseja excluir esta competência?</h5>
            <p className="text-muted">Esta ação não pode ser desfeita.</p>
          </Modal.Body>
          <Modal.Footer className="bg-light">
            <Button variant="secondary" onClick={handleCloseDelete} size="lg">
              <i className="fas fa-times mr-2"></i>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDeleteCompetencia} size="lg">
              <i className="fas fa-trash mr-2"></i>
              Confirmar Exclusão
            </Button>
          </Modal.Footer>
        </Modal>
      </S.MainWrapper>
    </Template>
  );
}

export default Competencias;
